import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import type { AnchorHTMLAttributes } from "react";
import ReactMarkdown from "react-markdown";
import { Link as RouterLink } from "react-router";
import rehypeRaw from "rehype-raw";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import remarkGfm from "remark-gfm";

const sanitizeSchema = {
  ...defaultSchema,
  tagNames: [...(defaultSchema.tagNames ?? []), "table", "thead", "tbody", "tr", "th", "td", "a"],
  attributes: {
    ...defaultSchema.attributes,
    table: [...(defaultSchema.attributes?.table ?? []), "align"],
    th: [...(defaultSchema.attributes?.th ?? []), "align", "colspan", "rowspan", "scope"],
    td: [...(defaultSchema.attributes?.td ?? []), "align", "colspan", "rowspan", "headers"],
    a: [...(defaultSchema.attributes?.a ?? []), "href", "title", "target", "rel"],
  },
};

function isInternalHref(href: string | undefined): href is string {
  return Boolean(href) && href!.startsWith("/");
}

/**
 * Wiki articles link to each other constantly, and those links have to navigate in place.
 *
 * The shared `MarkdownRenderer` opens every link in a new tab, which is right for a chat message
 * that mostly contains external URLs and wrong here — following a cross-reference between two
 * procedure pages should not spawn a tab. In-app paths go through the router; anything else keeps
 * the new-tab-with-noopener treatment.
 */
function ArticleLink({ href, children, ...rest }: AnchorHTMLAttributes<HTMLAnchorElement>) {
  if (isInternalHref(href)) {
    return (
      <Link component={RouterLink} to={href} {...rest}>
        {children}
      </Link>
    );
  }

  return (
    <Link href={href} rel="noopener noreferrer" target="_blank" {...rest}>
      {children}
    </Link>
  );
}

type Props = Readonly<{ children: string }>;

export default function ArticleMarkdown({ children }: Props) {
  return (
    <Box
      sx={{
        "& h1": { fontSize: "2rem", fontWeight: 400, mb: 2, mt: 0 },
        "& h2": { fontSize: "1.5rem", fontWeight: 400, mb: 1.5, mt: 4 },
        "& h3": { fontSize: "1.2rem", fontWeight: 500, mb: 1, mt: 3 },
        "& p": { lineHeight: 1.7, mb: 2 },
        "& ul, & ol": { lineHeight: 1.7, mb: 2, pl: 3 },
        "& li": { mb: 0.5 },
        "& blockquote": {
          borderLeft: 4,
          borderColor: "divider",
          color: "text.secondary",
          ml: 0,
          pl: 2,
          my: 2,
        },
        "& code": {
          backgroundColor: "action.hover",
          borderRadius: 0.5,
          fontSize: "0.9em",
          px: 0.5,
          py: 0.25,
        },
        // Wide tables are common in these pages - fee schedule tiers, onboarding status. Let the
        // table scroll inside the article rather than pushing the whole page sideways.
        "& table": { borderCollapse: "collapse", display: "block", mb: 3, overflowX: "auto", width: "100%" },
        "& th, & td": { border: 1, borderColor: "divider", p: 1, textAlign: "left" },
        "& th": { backgroundColor: "action.hover", fontWeight: 600 },
      }}
    >
      <ReactMarkdown
        components={{ a: ArticleLink }}
        rehypePlugins={[rehypeRaw, [rehypeSanitize, sanitizeSchema]]}
        remarkPlugins={[remarkGfm]}
      >
        {children}
      </ReactMarkdown>
    </Box>
  );
}
